/**
 * The stages a chat turn passes through, and the clock that measures them.
 *
 * Kept apart from `chatTurn.ts` for two reasons. The phase names are a
 * bounded enumeration the server turns into a metric label, and `otel.js`
 * has to hold its own copy (it cannot import TypeScript) — so the list needs
 * to sit in a module the drift-guard test can import without dragging
 * Office.js and the whole turn loop in with it. And the clock is worth
 * testing on a hand-advanced timer rather than through a real turn.
 *
 * Carries no timings of its own beyond durations in milliseconds: no cell
 * values, no prompt text (ADR-0002).
 */

/**
 * What a turn is busy with right now, so the UI can say something more
 * useful than "..." while the user waits:
 * - "reading-workbook": gathering the spreadsheet snapshot and locale.
 * - "thinking": the model is composing its first reply.
 * - "running-actions": parsed actions are being applied to the workbook.
 * - "reviewing": the model is reading back the results of those actions.
 *
 * Declared as data first so the set can be iterated — the server needs the
 * values, not just the type.
 */
export const TURN_PHASES = [
  "reading-workbook",
  "thinking",
  "running-actions",
  "reviewing",
] as const;

export type TurnPhase = (typeof TURN_PHASES)[number];

/** How long a turn spent in each phase, in milliseconds, summed over iterations. */
export type PhaseDurations = Record<TurnPhase, number>;

export function emptyPhaseDurations(): PhaseDurations {
  return Object.fromEntries(
    TURN_PHASES.map((phase) => [phase, 0]),
  ) as PhaseDurations;
}

export interface PhaseClock {
  /** Closes the phase currently being charged and starts charging `phase`. */
  enter(phase: TurnPhase): void;
  /** Closes the open phase. Idempotent, so an exit path may call it blindly. */
  stop(): void;
  /** The totals so far. A snapshot: editing it does not affect the clock. */
  totals(): PhaseDurations;
}

/**
 * A stopwatch with one hand: exactly one phase is being charged at a time,
 * and entering the next closes the last. Modelled that way because a turn is
 * sequential — overlapping phases would double-count the turn's own duration
 * and make "where did the time go" unanswerable.
 *
 * The corollary is that a phase's total runs until the *next* phase opens,
 * not until its own work returns: parsing the reply and rendering it are
 * charged to the phase that produced them. That is deliberate — it keeps the
 * phases summing to the turn instead of leaving unattributed gaps — and the
 * bookkeeping is negligible beside the model call it follows. Whether it
 * stays negligible is exactly what these totals are there to show.
 *
 * `now` is injectable so tests can advance time by hand.
 */
export function createPhaseClock(now: () => number = Date.now): PhaseClock {
  const totals = emptyPhaseDurations();
  let open: TurnPhase | undefined;
  let openedAt = 0;

  const close = () => {
    if (open === undefined) return;
    totals[open] += now() - openedAt;
    open = undefined;
  };

  return {
    enter(phase) {
      close();
      open = phase;
      openedAt = now();
    },
    stop: close,
    totals: () => ({...totals}),
  };
}
